import type { SessionResultOut } from "../contract/api";
import { t } from "../contract/strings";
import { summaryPendingOf } from "../state/store";
import { SpeakerNotesIcon, cx } from "../ui";
import { ActionsCard } from "./result-actions";
import type { ActionTicks } from "./result-actions-state";
import {
  LABEL,
  SECTION_GAP,
  resultHighlights,
  summaryBody,
  summaryFailureInTabs,
  summaryStatusMessage,
} from "./result-model";
import { PeopleSection } from "./result-person-card";

/**
 * The summary card: the stored text, or the line that stands in while it is written, or
 * the plain sentence that says it could not be. Under it, the key points as bullets
 * (#772).
 */
export function SummaryCard({ result }: { result: SessionResultOut }) {
  const pending = !result.summary && summaryPendingOf(result);
  const failed = result.summary_status === "failed";
  const status = summaryStatusMessage(result);
  const body = summaryBody(result);
  const failure = summaryFailureInTabs(result);
  // Only beside a written summary; a meeting summarised before #772 has none.
  const highlights = result.summary ? resultHighlights(result) : [];
  return (
    <section className="p-4 bg-surface border border-line rounded-card flex flex-col gap-stack">
      <div className="flex items-center gap-tight">
        <SpeakerNotesIcon className="size-icon-xs text-accent flex-none" />
        <span className={cx(LABEL, "text-label text-ink-soft")}>{t("result.summary")}</span>
        {pending ? (
          <span className={cx(LABEL, "text-label ml-auto text-warn-ink italic")}>{status}</span>
        ) : failed ? (
          <span className={cx(LABEL, "text-label ml-auto text-danger")}>{status}</span>
        ) : null}
      </div>
      {failure ? <p className="m-0 text-note text-ink-soft">{failure}</p> : null}
      {body ? (
        // The summary's own language (#772), so a screen reader and the fonts read it
        // right. Only on the stored text: the stand-in while it is written is our copy.
        <p
          data-summary-text
          lang={result.summary ? (result.language ?? undefined) : undefined}
          className={cx("m-0 text-summary text-ink", pending && "text-ink-soft italic")}
        >
          {body}
        </p>
      ) : null}
      {highlights.length ? (
        <ul
          data-highlights
          lang={result.language ?? undefined}
          aria-label={t("result.highlights_aria")}
          className="m-0 p-0 list-none flex flex-col gap-nudge"
        >
          {highlights.map((text, index) => (
            <li key={index} className="flex items-baseline gap-tight text-note leading-normal text-ink">
              <span aria-hidden="true" className="flex-none font-bold text-accent">
                •
              </span>
              <span className="min-w-0">{text}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * Quick: the meeting at a glance (#766) -- the summary (with its key points, #772), then
 * the "Actions" checklist, then the Speakers section (a mini card per person: minutes
 * spoken, topics, their quote and follow-up; no share bar). The meeting's topics are in
 * the details card above the tabs.
 */
export function QuickTab({
  result,
  ticks,
  onJump,
  onSpeakerRenamed,
}: {
  result: SessionResultOut;
  /** The meeting's shared action ticks (#786). */
  ticks: ActionTicks;
  onJump: (time: number) => void;
  onSpeakerRenamed: (next: SessionResultOut) => void;
}) {
  // A result written before the field existed omits `cards`; every action comes from one.
  const cards = result.cards ?? [];
  const showSummary =
    Boolean(result.summary) || summaryPendingOf(result) || result.summary_status === "failed";

  if (!showSummary && !cards.length) {
    return <p className="m-0 p-4 bg-surface border border-line rounded-card text-note text-ink-soft">{t("result.quick_empty")}</p>;
  }

  return (
    <div className={cx("flex flex-col", SECTION_GAP)}>
      {showSummary ? <SummaryCard result={result} /> : null}
      <ActionsCard key={result.session_id} result={result} ticks={ticks} onJump={onJump} />
      <PeopleSection result={result} onJump={onJump} onSpeakerRenamed={onSpeakerRenamed} />
    </div>
  );
}
